import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchDietaryPreferences, deleteDietaryPreference, searchDietaryPreferences, bulkDeleteDietaryPreferences, bulkUpdateDietaryPreferenceStatus } from "./dietaryPreferenceSlice";
import Swal from 'sweetalert2';
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import log from "../../logger";
import ReactTable from "../../components/ReactTable";
import { paths } from "../../routes/helpers";
import { getImageUrl } from "../../utils/urlUtils";
import useDebounce from "../../hooks/useDebounce";

const DietaryPreferenceList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total, pages } = useSelector(
    (state) => state.dietaryPreference
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDietaryPreference, setSelectedDietaryPreference] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchDietaryPreferences({ keyword: debouncedSearchTerm, page: 1, limit: currentLimit }));
    } else {
      dispatch(fetchDietaryPreferences({ page: currentPage, limit: currentLimit }));
    }
  }, [dispatch, currentPage, currentLimit, debouncedSearchTerm]);

  // Reset current page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  const handleDelete = (dietaryPreferenceId) => {
    setShowDeleteModal(true);
    setSelectedDietaryPreference(dietaryPreferenceId);
  };

  const confirmDelete = async () => {
    if (selectedDietaryPreference) {
      try {
        await dispatch(deleteDietaryPreference(selectedDietaryPreference)).unwrap();
        toast.success("Dietary preference deleted successfully!");
        log.info("Dietary preference deleted:", selectedDietaryPreference);
      } catch (err) {
        toast.error(err || "Failed to delete dietary preference");
        log.error("Error deleting dietary preference:", err);
      } finally {
        setShowDeleteModal(false);
        setSelectedDietaryPreference(null);
      }
    }
  };

  const handleEdit = (dietaryPreferenceId) => {
    navigate(paths.private.dietaryPreference.edit(dietaryPreferenceId));
  };

  const handleCreateDietaryPreference = () => {
    navigate(paths.private.dietaryPreference.create());
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1);
    if (debouncedSearchTerm) {
      dispatch(searchDietaryPreferences({ keyword: debouncedSearchTerm, page: 1, limit: newLimit }));
    } else {
      dispatch(fetchDietaryPreferences({ page: 1, limit: newLimit }));
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} dietary preference${ids.length > 1 ? 's' : ''}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await dispatch(bulkDeleteDietaryPreferences(ids)).unwrap();
      Swal.fire({ title: 'Deleted!', text: `${ids.length} dietary preference${ids.length > 1 ? 's' : ''} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Some deletions failed', text: err || 'One or more items are still in use elsewhere and were not deleted.', icon: 'error' });
    } finally {
      clearSelection();
    }
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 1;
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} dietary preference${ids.length > 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateDietaryPreferenceStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} dietary preference${ids.length > 1 ? 's' : ''} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const formatTableData = (dietaryPreferences) => {
    return dietaryPreferences.map((dietaryPreference) => ({
      _id: dietaryPreference._id,
      label: dietaryPreference.label || '-',
      image: getImageUrl(dietaryPreference.image) || null,
      status: dietaryPreference.status ? 'Active' : 'Inactive',
      createdAt: dietaryPreference.createdAt ? new Date(dietaryPreference.createdAt).toLocaleString() : '-',
      updatedAt: dietaryPreference.updatedAt ? new Date(dietaryPreference.updatedAt).toLocaleString() : '-',
    }));
  };

  const headers = [
    { label: 'Image', key: 'image' },
    { label: 'Label', key: 'label' },
    { label: 'Status', key: 'status' },
    { label: 'Created At', key: 'createdAt' },
    { label: 'Updated At', key: 'updatedAt' },
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  if (error) return (
    <div className="p-4">
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Dietary Preferences</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateDietaryPreference}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New Dietary Preference
        </button>
        <div className="text-sm text-gray-600">
          {searchTerm ? `Found ${total} dietary preferences` : `Total: ${total} dietary preferences`}
        </div>
      </div>

      <ReactTable
        headers={headers}
        data={formatTableData(items)}
        actions={actions}
        onSearch={handleSearch}
        searchDelay={1000}
        searchValue={searchTerm}
        loading={loading}
        pagination={items.length > 0 ? { page: currentPage, limit: currentLimit, total, pages } : null}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange(1) },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange(0) },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {/* No results message */}
      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
          <p className="text-gray-500">
            {searchTerm ? `No dietary preferences found for "${searchTerm}"` : 'No dietary preferences found'}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Try a different search term' : 'Click the Create New button to add one'}
          </p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this dietary preference?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default DietaryPreferenceList;